// Chakra imports
import { SimpleGrid, Text, useColorModeValue, Spinner, Center, Box, Flex, Alert, AlertIcon, AlertDescription, HStack, Badge, VStack, Progress, Collapse, List, ListItem, ListIcon, Divider, Button, Icon, Heading } from "@chakra-ui/react";
import { CheckCircleIcon, WarningIcon, InfoOutlineIcon, ChevronRightIcon, ChevronDownIcon } from "@chakra-ui/icons";
// Custom components
import Card from "components/card/Card.js";
import React, { useEffect, useState } from "react";
import { useTranslation } from 'react-i18next';
import Information from "views/admin/profile/components/Information";

import constants from "providers/Constants";
import useAuthentication from "providers/Authentication";

// Assets
export default function BusinessInformation(props) {

  const authentication = useAuthentication();
  const { t } = useTranslation();

  const { user, profile, developer, countries, uploads, ...rest } = props;
  const [loading, setLoading] = useState(false);
  const [business_verification, setBusinessVerification] = useState([]);
  const [business_categories, setBusinessCategories] = useState([]);
  const [location, setLocation] = useState(profile?.business_upload_location || {});
  const [capturing, setCapturing] = useState(false);

  // Chakra Color Mode
  const textColorPrimary = useColorModeValue("secondaryGray.900", "white");
  const textColorSecondary = "gray.400";
  const cardShadow = useColorModeValue(
    "0px 18px 40px rgba(112, 144, 176, 0.12)",
    "unset"
  );
  const innerBg = useColorModeValue('white', 'navy.700');
  const locationBg = useColorModeValue('gray.50', 'navy.800');

  useEffect(() => {
    if (profile?.business_upload_country) { getDocumentCategories(profile?.business_upload_country); }
  }, [user, profile, developer, countries, uploads]);

  // Capture location
  async function captureLocation() {
    try {
      setCapturing(true);
      const captured = await constants.getCurrentPosition();
      if (!captured || !captured.latitude || !captured.longitude) {
        setCapturing(false);
        return;
      }
      setLocation(captured);
      setCapturing(false);
      updateProfile('business_upload_location', captured);
    } catch { setCapturing(false); }
  }

  return (
    <Card mb={{ base: "0px", "2xl": "20px" }} {...rest}>
      {loading ? (
        <Center w='100%' minH={rest?.minH || '220px'}>
          <Spinner size="xl" />
        </Center>
      ) : (
        <>
          <StatusBanner
            status={profile?.business_upload_status}
            message={profile?.business_upload_status_message}
            onAction={submitDocument}
            submittedAt={profile?.business_upload_timestamp}
            checklist={{
              country: !!profile?.business_upload_country,
              type: !!profile?.business_reg_type,
              proofCert: !!profile?.business_certificate_upload_photo,
              proofOwner: !!profile?.business_ownership_upload_photo,
              location: !!profile?.business_upload_location && !!profile?.business_upload_location?.latitude && !!profile?.business_upload_location?.longitude,
            }}
          />
          <SimpleGrid columns='2' gap='20px'>
            <Information
              boxShadow={cardShadow}
              select={true}
              disabled={isLocked(profile?.business_upload_status)}
              select_array={countries}
              title={t('Business Registration Country')}
              value={`${profile?.business_upload_country || t('N/A')}`}
              onChange={e => {
                profile['business_upload_country'] = e;
                updateProfile('business_upload_country', e);
              }}
            />
            <Information
              boxShadow={cardShadow}
              disabled={isLocked(profile?.business_upload_status)}
              title={t('Business Name (as it appears on your registration documents)')}
              value={`${profile?.business_name || t('N/A')}`}
              onChange={e => {
                profile['business_name'] = e;
                updateProfile('business_name', e);
              }}
            />
            {
              profile?.business_upload_country ? (
                <Information
                  boxShadow={cardShadow}
                  select={true}
                  disabled={isLocked(profile?.business_upload_status)}
                  title={t('Business Registration Type')}
                  select_array={business_verification}
                  value={`${profile?.business_reg_type || t('N/A')}`}
                  onChange={e => {
                    profile['business_reg_type'] = e;
                    updateProfile('business_reg_type', e);
                  }}
                />
              ) : null
            }
            <Information
              boxShadow={cardShadow}
              disabled={isLocked(profile?.business_upload_status)}
              title={t('Registration Number (as it appears on your registration documents)')}
              value={`${profile?.business_reg_number || t('N/A')}`}
              onChange={e => {
                profile['business_reg_number'] = e;
                updateProfile('business_reg_number', e);
              }}
            />
            {
              profile?.business_upload_country ? (
                <>
                  <Information
                    boxShadow={cardShadow}
                    select={true}
                    disabled={isLocked(profile?.business_upload_status)}
                    select_array={business_categories}
                    title={t('Business Category')}
                    value={`${profile?.business_category || t('N/A')}`}
                    onChange={e => {
                      profile['business_category'] = e;
                      updateProfile('business_category', e);
                    }}
                  />
                  <Information
                    boxShadow={cardShadow}
                    title={t('Tax ID')}
                    disabled={isLocked(profile?.business_upload_status)}
                    value={`${profile?.business_tax_id || t('N/A')}`}
                    onChange={e => {
                      profile['business_tax_id'] = e;
                      updateProfile('business_tax_id', e);
                    }}
                  />
                  <Information
                    boxShadow={cardShadow}
                    title={t('Business Certificate/Incorporation Document Upload')}
                    upload={true}
                    disabled={isLocked(profile?.business_upload_status)}
                    upload_array={uploads}
                    hint={t('HINT: Select file from your uploads on BlockMap. Use the file uploader tool to upload files')}
                    value={`${profile?.business_certificate_upload_photo || t('N/A')}`}
                    onChange={e => {
                      profile['business_certificate_upload_photo'] = e;
                      updateProfile('business_certificate_upload_photo', e);
                    }}
                  />
                  <Information
                    boxShadow={cardShadow}
                    title={t('Business Ownership Document Upload')}
                    upload={true}
                    disabled={isLocked(profile?.business_upload_status)}
                    upload_array={uploads}
                    hint={t('HINT: Select file from your uploads on BlockMap. Use the file uploader tool to upload files')}
                    value={`${profile?.business_ownership_upload_photo || t('N/A')}`}
                    onChange={e => {
                      profile['business_ownership_upload_photo'] = e;
                      updateProfile('business_ownership_upload_photo', e);
                    }}
                  />
                  <Information
                    boxShadow={cardShadow}
                    title={t('Business Email')}
                    value={`${developer?.business_email || t('N/A')}`}
                    onChange={e => {
                      developer['business_email'] = e;
                      updateProfile('business_email', e);
                    }}
                  />
                  <Information
                    boxShadow={cardShadow}
                    title={t('Business Phone')}
                    value={`${developer?.business_phone || t('N/A')}`}
                    onChange={e => {
                      developer['business_phone'] = e;
                      updateProfile('business_phone', e);
                    }}
                  />
                  <Information
                    boxShadow={cardShadow}
                    title={t('Business Description')}
                    value={`${developer?.business_description || t('N/A')}`}
                    onChange={e => {
                      developer['business_description'] = e;
                      updateProfile('business_description', e);
                    }}
                  />
                  <Information
                    boxShadow={cardShadow}
                    title={t('Website')}
                    value={`${developer?.business_website || t('N/A')}`}
                    onChange={e => {
                      developer['business_website'] = e;
                      updateProfile('business_website', e);
                    }}
                  />
                  <Information
                    boxShadow={cardShadow}
                    title={t('Date of Registration')}
                    date
                    disabled={isLocked(profile?.business_upload_status)}
                    value={`${profile?.business_date_of_registration || t('N/A')}`}
                    onChange={e => {
                      profile['business_date_of_registration'] = e;
                      updateProfile('business_date_of_registration', e);
                    }}
                  />
                  {/* Location capture */}
                  <Box gridColumn="1 / span 2" my={2}>
                    <Card mb={{ base: "0px", "2xl": "0px" }} boxShadow={cardShadow}>
                      <Flex direction="column" gap={4} p={4} bg={innerBg} borderRadius="12px">
                        <Heading size="sm">{t('Use business location')}</Heading>
                        <Text color={textColorSecondary}>{t('Capture coordinates to support business verification.')}</Text>
                        <Button
                          leftIcon={
                            <Icon
                              as={() => (
                                (location && location.latitude)
                                  ? (
                                    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                                      <path d="M17.65 6.35A7.95 7.95 0 0 0 12 4V1L7 6l5 5V8a5 5 0 1 1-5 5H5a7 7 0 1 0 12.65-6.65z" fill="currentColor" />
                                    </svg>
                                  )
                                  : (
                                    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                                      <path d="M11 2h2v3a7 7 0 0 1 6 6h3v2h-3a7 7 0 0 1-6 6v3h-2v-3a7 7 0 0 1-6-6H2v-2h3a7 7 0 0 1 6-6V2zm1 5a5 5 0 1 0 0 10 5 5 0 0 0 0-10z" fill="currentColor" />
                                    </svg>
                                  )
                              )}
                            />
                          }
                          onClick={captureLocation}
                          isLoading={capturing}
                          variant='brand'
                          isDisabled={isLocked(profile?.business_upload_status)}
                          width="220px"
                        >
                          {location && location.latitude ? t('Refresh location') : t('Capture location')}
                        </Button>

                        {location && location.latitude && (
                          <Box borderRadius="12px" borderWidth="1px" p={4} bg={locationBg}>
                            <Flex justify="space-between">
                              <Box>
                                <Text fontSize='xs' color='gray.500'>{t('LATITUDE')}</Text>
                                <Text fontWeight='bold' fontSize='lg'>{location.latitude}</Text>
                              </Box>
                              <Box>
                                <Text fontSize='xs' color='gray.500'>{t('LONGITUDE')}</Text>
                                <Text fontWeight='bold' fontSize='lg'>{location.longitude}</Text>
                              </Box>
                              <Box>
                                <Text fontSize='xs' color='gray.500'>{t('ACCURACY')}</Text>
                                <Text fontWeight='bold' fontSize='lg'>
                                  {t('± {{meters}} m', { meters: Math.round(location.accuracy || 0) })}
                                </Text>
                              </Box>
                            </Flex>
                            <Text fontSize='sm' color='gray.500' mt={3}>
                              {t('Captured {{datetime}}', { datetime: new Date(location.timestamp).toLocaleString() })}
                            </Text>
                            {capturing && (
                              <Text mt={3} fontSize='sm' color='gray.500'>
                                {t('Looking up address...')}
                              </Text>
                            )}
                            <Box mt={3} p={3} borderRadius='md' bg={innerBg}>
                              <Text fontSize='sm' color='gray.500'>
                                {t('Your location stays encrypted with your KYC.')}
                              </Text>
                            </Box>
                          </Box>
                        )}
                      </Flex>
                    </Card>
                  </Box>
                </>
              ) : null
            }
          </SimpleGrid>
        </>
      )}
    </Card>
  );

  // Get document categories
  async function getDocumentCategories(country_iso_code_2) {
    setLoading(true);
    authentication.getVerificationTypes(country_iso_code_2, (error, response) => {
      setLoading(false);
      if (error) {
        constants.showToast(
          "error",
          t('FAILED'),
          error?.message || t('Snap! Something went wrong, Try again')
        );
      } else {
        const business_verification = response?.business_verification?.map(entry => entry.name) || [];
        const business_categories = response?.business_categories?.map(entry => entry.name) || [];
        setBusinessVerification(business_verification);
        setBusinessCategories(business_categories);
      }
    });
  }

  // Update profile
  async function updateProfile(key, value) {
    setLoading(true);
    const data = {
      [key]: value,
    }
    authentication.updateUser(data, (error, data) => {
      setLoading(false);
      if (error) {
        constants.showToast(
          "error",
          t('FAILED'),
          error?.message || t('Snap! Something went wrong, Try again')
        );
      } else {
        constants.showToast(
          "success",
          t('SUCCESS'),
          t('Profile updated successfully')
        );
      }
    });
  }

  // Submit business verification
  async function submitDocument() {
    const isValid = validateSubmitBusiness();
    if (!isValid) return;
    setLoading(true);
    authentication.updateUser({
      action: 'business_verification', business_upload_country: profile?.business_upload_country, business_name: profile?.business_name,
      business_reg_number: profile?.business_reg_number, business_category: profile?.business_category, business_reg_type: profile?.business_reg_type,
      business_tax_id: profile?.business_tax_id, business_certificate_upload_photo: profile?.business_certificate_upload_photo,
      business_ownership_upload_photo: profile?.business_ownership_upload_photo, business_date_of_registration: profile?.business_date_of_registration,
      business_upload_location: profile?.business_upload_location, business_upload_timestamp: Date.now(),
      business_upload_verified: false, business_upload_status: 'pending', business_upload_status_message: 'Business verification is pending approval!'
    }, (error, data) => {
      setLoading(false);
      if (error) {
        constants.showToast(
          "error",
          t('FAILED'),
          error?.message || t('Snap! Something went wrong, Try again')
        );
      } else {
        constants.showToast(
          "success",
          t('SUCCESS'),
          t('Verification document submitted successfully!')
        );
        window.location.reload();
      }
    });
  }

  // Validate business submit
  function validateSubmitBusiness() {
    const checks = [
      [!!profile?.business_upload_country, t('Please select registration country!')],
      [!!profile?.business_name, t('Please enter business name!')],
      [!!profile?.business_reg_type, t('Please select registration type!')],
      [!!profile?.business_reg_number, t('Please enter registration number!')],
      [!!profile?.business_category, t('Please select business category!')],
      [!!profile?.business_certificate_upload_photo, t('Please upload business certificate!')],
      [!!profile?.business_ownership_upload_photo, t('Please upload ownership document!')],
      [!!profile?.business_date_of_registration, t('Please select date of registration!')],
      [!!profile?.business_upload_location, t('Please capture business location!')],
      [!!profile?.business_upload_location?.latitude, t('Please capture business location!')],
      [!!profile?.business_upload_location?.longitude, t('Please capture business location!')],
      [!!developer?.business_email, t('Please enter business email!')],
      [!!developer?.business_phone, t('Please enter business phone!')],
      [!!developer?.business_description, t('Please enter business description!')],
      [!!developer?.business_website, t('Please enter business website!')],
    ];
    const errors = checks.filter(([ok]) => !ok).map(([, msg]) => msg);
    if (errors.length) {
      constants.showToast(
        "error",
        t('FAILED'),
        errors.join('\n')
      );
      return false;
    }
    return true;
  }
}

// Check list item
function ChecklistItem({ ok, label }) {
  return (
    <ListItem>
      <HStack>
        <ListIcon as={ok ? CheckCircleIcon : WarningIcon} color={ok ? 'green.400' : 'orange.300'} />
        <Text>{label}</Text>
      </HStack>
    </ListItem>
  );
}

// Check if status is locked
function isLocked(status) {
  const s = (status || '').toLowerCase();
  return s === 'verified' || s === 'pending';
}

// Status banner (business)
function StatusBanner({ status, message, onAction, submittedAt, checklist }) {
  const { t } = useTranslation();
  const s = (status || '').toLowerCase();
  const meta = (() => {
    switch (s) {
      case 'verified':
        return { label: t('Verified'), chakraStatus: 'success', colorScheme: 'green', description: message ? t(message) : t('Your business is verified. No further action required.') };
      case 'pending':
        return { label: t('Pending'), chakraStatus: 'warning', colorScheme: 'orange', description: message ? t(message) : t('Your business verification is under review.') };
      case 'rejected':
      case 'failed':
        return { label: t('Rejected'), chakraStatus: 'error', colorScheme: 'red', description: message ? t(message) : t('We could not verify your business. Please review and resubmit.'), actionLabel: t('Resubmit') };
      default:
        return { label: t('Not Submitted'), chakraStatus: 'info', colorScheme: 'blue', description: message ? t(message) : t('Fill in your business details and upload required documents to start verification.'), actionLabel: t('Submit') };
    }
  })();

  const progress = s === 'verified' ? 100 : s === 'pending' ? 66 : s === 'rejected' || s === 'failed' ? 40 : 15;
  const [expanded, setExpanded] = React.useState(false);
  const [showTips, setShowTips] = React.useState(false);
  const textColorSecondary = 'gray.400';

  return (
    <Box mb={4}>
      <Alert status={meta.chakraStatus} variant="left-accent" borderRadius="10px" alignItems="flex-start" py={4} px={4}>
        <AlertIcon />
        <Box flex="1">
          <Flex justify="space-between" align="center" gap={3} w="100%">
            <HStack spacing={3} align="center">
              <Badge colorScheme={meta.colorScheme} borderRadius="full" px={2} py={0.5}>{meta.label}</Badge>
            </HStack>
            {meta.actionLabel && (
              <Button size="sm" colorScheme={meta.colorScheme} onClick={onAction} isDisabled={!onAction}>
                {meta.actionLabel} →
              </Button>
            )}
          </Flex>
          <Flex justify="space-between" align="center" gap={3} mt={1} w="100%">
            <AlertDescription color={textColorSecondary} fontSize="sm" m={0}>
              {meta.description}
              {constants.formatDate(submittedAt) && (
                <Text as="span" ml={2} fontSize="xs" color={textColorSecondary}>• {t('Last submitted:')} {constants.formatDate(submittedAt)}</Text>
              )}
            </AlertDescription>
            <Button variant="ghost" size="sm" rightIcon={expanded ? <ChevronDownIcon /> : <ChevronRightIcon />} onClick={() => setExpanded(v => !v)}>
              {t('Details')}
            </Button>
          </Flex>
          <Collapse in={expanded} animateOpacity>
            <VStack align="stretch" mt={3} spacing={3}>
              <Progress value={progress} colorScheme={meta.colorScheme} borderRadius="full" size="xs" />
              <List spacing={1} fontSize="sm" color={textColorSecondary}>
                <ChecklistItem ok={checklist?.country} label={t('Registration Country')} />
                <ChecklistItem ok={checklist?.type} label={t('Registration Type')} />
                <ChecklistItem ok={checklist?.proofCert} label={t('Certificate Uploaded')} />
                <ChecklistItem ok={checklist?.proofOwner} label={t('Ownership Proof Uploaded')} />
              </List>
              <Divider />
              <HStack fontSize="sm" color={textColorSecondary} justify="space-between">
                <HStack>
                  <InfoOutlineIcon />
                  <Text>{t('Tip: ensure your documents are clear and match your registration details.')}</Text>
                </HStack>
                <Button variant="link" colorScheme={meta.colorScheme} size="sm" onClick={() => setShowTips(v => !v)}>
                  {showTips ? t('Hide tips') : t('Show tips')}
                </Button>
              </HStack>
              <Collapse in={showTips} animateOpacity>
                <Text fontSize="sm" color={textColorSecondary}>
                  {t('Use good lighting and upload both certificate and ownership proof. Include registration number and date.')}
                </Text>
              </Collapse>
            </VStack>
          </Collapse>
        </Box>
      </Alert>
    </Box>
  );
}

